import { useState } from 'react';
import vedicImage from '../images/vedic-mathematics.jpg';
import abacusImage from '../images/abacus-knowledge.webp';
import yogaImage from '../images/yoga-children.jpeg';

const topics = [
  {
    id: 'vedic',
    label: 'Vedic Maths',
    icon: '✦',
    image: vedicImage,
    title: 'See numbers as patterns, not problems.',
    description:
      'Vedic Mathematics is a collection of calculation approaches inspired by Indian mathematical traditions. It encourages flexible thinking by showing learners more than one way to work with numbers.',
    learns: [
      'Mental calculation strategies',
      'Number patterns and relationships',
      'Speed with accuracy and checking',
      'Confidence in approaching arithmetic',
    ],
    insightTitle: 'Try this pattern',
    insight:
      'For 98 × 97, think “near 100.” The deficits are 2 and 3. Subtract crosswise: 98 − 3 = 95. Multiply the deficits: 2 × 3 = 06. The answer is 9,506.',
    note: 'Understanding why a method works is always more important than memorising a shortcut.',
  },
  {
    id: 'abacus',
    label: 'Abacus',
    icon: '◉',
    image: abacusImage,
    title: 'Make place value visible and hands-on.',
    description:
      'An abacus represents numbers with movable beads. Children can physically see units, tens, hundreds and beyond, turning an abstract idea into a concrete learning experience.',
    learns: [
      'Place value and number sense',
      'Coordinated listening and movement',
      'Accurate addition and subtraction',
      'Mental visualisation with practice',
    ],
    insightTitle: 'A strong first step',
    insight:
      'Begin by asking a learner to show the same number in different ways—for example, 24 as 2 tens and 4 ones. Clear bead movement and correct place value should come before speed.',
    note: 'Short, regular practice is generally more useful than one long session.',
  },
  {
    id: 'yoga',
    label: 'Yoga & Mindfulness',
    icon: '☼',
    image: yogaImage,
    title: 'Build awareness through movement and breath.',
    description:
      'Child-friendly yoga combines gentle postures, breathing awareness and quiet attention. The goal is not perfect poses—it is learning to notice the body, move comfortably and pause with intention.',
    learns: [
      'Body awareness and coordination',
      'Comfortable breathing habits',
      'Balance, mobility and posture',
      'Calm transitions between activities',
    ],
    insightTitle: 'A one-minute reset',
    insight:
      'Sit comfortably, relax the shoulders and take five slow breaths. Count each exhale from one to five, then notice how the body feels before returning to study or play.',
    note: 'Children should practise with appropriate supervision, avoid forced stretches and stop if any movement causes pain or dizziness.',
  },
];

function KnowledgeHub() {
  const [activeTopic, setActiveTopic] = useState(topics[0].id);
  const topic = topics.find((item) => item.id === activeTopic);

  return (
    <section className="knowledge" id="knowledge" aria-labelledby="knowledge-title">
      <div className="container">
        <div className="section-title">
          <span className="eyebrow">Knowledge hub</span>
          <h2 id="knowledge-title">Learn a little before the first class.</h2>
          <p>
            Explore the ideas behind three of our core programs and discover
            how each one supports a different part of learning.
          </p>
        </div>

        <div className="knowledge-shell">
          <div className="knowledge-tabs" role="tablist" aria-label="Learning topics">
            {topics.map((item) => (
              <button
                className={`knowledge-tab${item.id === activeTopic ? ' active' : ''}`}
                id={`tab-${item.id}`}
                key={item.id}
                type="button"
                role="tab"
                aria-selected={item.id === activeTopic}
                aria-controls={`panel-${item.id}`}
                onClick={() => setActiveTopic(item.id)}
              >
                <span aria-hidden="true">{item.icon}</span>
                {item.label}
              </button>
            ))}
          </div>

          <article
            className="knowledge-panel"
            id={`panel-${topic.id}`}
            role="tabpanel"
            aria-labelledby={`tab-${topic.id}`}
          >
            <div className="knowledge-image">
              <img src={topic.image} alt="" />
              <span>{topic.label}</span>
            </div>

            <div className="knowledge-copy">
              <h3>{topic.title}</h3>
              <p>{topic.description}</p>
              <h4>What learners develop</h4>
              <ul>
                {topic.learns.map((item) => <li key={item}>{item}</li>)}
              </ul>
            </div>

            <aside className="knowledge-insight">
              <span className="knowledge-spark" aria-hidden="true">✦</span>
              <small>{topic.insightTitle}</small>
              <p>{topic.insight}</p>
              <em>{topic.note}</em>
            </aside>
          </article>
        </div>
      </div>
    </section>
  );
}

export default KnowledgeHub;
